import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { supabase } from '@/lib/supabase';

interface VaultItem {
  id: string;
  product: string;
  code: string;
  weight: number;
  status: string;
  statusCode: string;
  storeDate: string;
  location: string;
}

export default function VaultPage() {
  const navigate = useNavigate();
  const [vaultItems, setVaultItems] = useState<VaultItem[]>([]);
  const [selected, setSelected] = useState<VaultItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setVaultItems([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('gold_assets')
        .select('*')
        .eq('user_id', userId)
        .neq('status', '已回购')
        .neq('status', '已提取')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setVaultItems(
        (data || []).map((row) => ({
          id: row.id,
          product: row.product || '',
          code: row.code || '',
          weight: Number(row.weight) || 0,
          status: row.status || '存储中',
          statusCode: row.status === '交割中'
          ? 'delivering'
          : row.status === '申请提货中'
            ? 'pending_pickup'
            : 'stored',
          storeDate: row.store_date || '',
          location: row.location || '',
        })),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalWeight = vaultItems.reduce((sum, item) => sum + item.weight, 0);
  const storedWeight = vaultItems
    .filter((item) => item.statusCode === 'stored')
    .reduce((sum, item) => sum + item.weight, 0);

  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      <SubHeader title="我的金库" />

      {/* Summary */}
      <div className="px-4 mt-4">
        <div className="bg-gradient-to-br from-brown-900 via-brown-800 to-brown-950 rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full border border-gold-400/20"></div>
          <div className="absolute bottom-0 left-1/3 w-24 h-24 rounded-full border border-gold-400/10"></div>
          <div className="relative">
            <p className="text-gold-300 text-xs mb-3 flex items-center gap-1.5">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-building-2-line"></i>
              </span>
              金库存储概览
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-white text-3xl font-bold">
                  {totalWeight}<span className="text-base font-normal text-gold-300">g</span>
                </p>
                <p className="text-gold-300/70 text-xs mt-1">累计持有黄金</p>
              </div>
              <div className="text-right">
                <p className="text-gold-400 text-2xl font-bold">
                  {storedWeight}<span className="text-sm font-normal">g</span>
                </p>
                <p className="text-gold-300/70 text-xs mt-1">安全存储中</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vault List */}
      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-brown-500 text-sm mb-3">金库数据加载失败</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              重新加载
            </button>
          </div>
        ) : vaultItems.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold-50 flex items-center justify-center">
              <i className="ri-vip-diamond-line text-gold-300 text-2xl"></i>
            </div>
            <p className="text-brown-400 text-sm">暂无存储黄金</p>
          </div>
        ) : (
          vaultItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setSelected(item)}
              className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 cursor-pointer text-left active:scale-[0.98] transition-transform"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0">
                <span className="w-7 h-7 flex items-center justify-center text-brown-950">
                  <i className="ri-vip-diamond-fill text-xl"></i>
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-brown-900 text-sm font-semibold truncate">{item.product}</h3>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${
                      item.statusCode === 'stored'
                        ? 'bg-gold-100 text-gold-700'
                        : 'bg-brown-100 text-brown-600'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>
                <p className="text-brown-400 text-xs mt-1 truncate">{item.location}</p>
                <p className="text-brown-400 text-[10px] mt-0.5">入库 {item.storeDate}</p>
              </div>
              <span className="w-4 h-4 flex items-center justify-center text-brown-300 flex-shrink-0">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            </button>
          ))
        )}
      </div>

      {/* Detail Sheet */}
      <BottomSheet open={!!selected} onClose={() => setSelected(null)} title="金条溯源">
        {selected && (
          <div className="px-4 pb-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0">
                <span className="w-8 h-8 flex items-center justify-center text-brown-950">
                  <i className="ri-vip-diamond-fill text-2xl"></i>
                </span>
              </div>
              <div>
                <h3 className="text-brown-900 text-base font-bold">{selected.product}</h3>
                <p className="text-gold-600 text-xs mt-1">{selected.weight}g · AU999.9</p>
              </div>
            </div>

            <div className="bg-brown-50 rounded-xl p-4 mb-4 text-center">
              <p className="text-brown-400 text-xs mb-2">一货一码 · 溯源编码</p>
              <p className="text-brown-900 text-xl font-bold tracking-widest">{selected.code}</p>
              <button className="mt-3 px-5 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-semibold cursor-pointer whitespace-nowrap active:scale-95 transition-transform">
                复制编码
              </button>
            </div>

            <div className="space-y-3">
              {[
                { label: '存储金库', value: selected.location },
                { label: '入库时间', value: selected.storeDate },
                { label: '当前状态', value: selected.status },
                { label: '成色', value: '99.99%' },
                { label: '认证机构', value: '国家金银制品质量监督检验中心' },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between">
                  <span className="text-brown-500 text-sm">{row.label}</span>
                  <span className="text-brown-900 text-sm font-medium text-right max-w-[60%]">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => { setSelected(null); navigate('/profile/sellback'); }}
                className="py-3 rounded-xl bg-red-50 text-red-600 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-95 transition-transform flex items-center justify-center gap-1.5"
              >
                <i className="ri-refund-line"></i>
                申请回购
              </button>
              <button
                onClick={() => { setSelected(null); navigate('/profile/pickup'); }}
                className="py-3 rounded-xl bg-gold-50 text-gold-700 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-95 transition-transform flex items-center justify-center gap-1.5"
              >
                <i className="ri-store-2-line"></i>
                申请提货
              </button>
            </div>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}